import React, { useState } from 'react';
import { X, Send, Store, User, Mail, Phone, MapPin, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import toast from 'react-hot-toast';

interface FranchiseApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FranchiseApplicationModal: React.FC<FranchiseApplicationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.city.trim()) {
      toast.error(t('franchiseApplication.requiredFields', 'Por favor completa todos los campos obligatorios'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t('franchiseApplication.invalidEmail', 'Por favor introduce un email válido'));
      return;
    }

    setLoading(true);

    try {
      // Save to Firestore
      await addDoc(collection(db, 'franchise_applications'), {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        city: formData.city.trim(),
        message: formData.message.trim() || null,
        status: 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast.success(t('franchiseApplication.success', 'Solicitud de franquicia enviada. Nos pondremos en contacto contigo pronto.'));

      // Reset form
      setFormData({ fullName: '', email: '', phone: '', city: '', message: '' });
      onClose();
    } catch (error) {
      console.error('Error submitting franchise application:', error);
      toast.error(t('franchiseApplication.error', 'Error al enviar la solicitud. Inténtalo de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="franchise-application-modal-title"
        className="bg-surface rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 id="franchise-application-modal-title" className="text-xl font-bold text-white">
                  {t('franchiseApplication.title', 'Abre tu Franquicia')}
                </h2>
                <p className="text-white/80 text-sm">
                  {t('franchiseApplication.subtitle', 'Únete a la familia Monkey Barber')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={t('common.close', 'Cerrar')}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <User className="w-4 h-4" />
              {t('franchiseApplication.fullName', 'Nombre Completo')} *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder={t('franchiseApplication.fullNamePlaceholder', 'Tu nombre y apellidos')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <Mail className="w-4 h-4" />
              {t('franchiseApplication.email', 'Email')} *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('franchiseApplication.emailPlaceholder', 'tu@email.com')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <Phone className="w-4 h-4" />
              {t('franchiseApplication.phone', 'Teléfono')} *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('franchiseApplication.phonePlaceholder', '+34 600 000 000')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition"
              required
            />
          </div>

          {/* City */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <MapPin className="w-4 h-4" />
              {t('franchiseApplication.city', 'Ciudad')} *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder={t('franchiseApplication.cityPlaceholder', '¿Dónde te gustaría abrir tu barbería?')}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition"
              required
            />
          </div>

          {/* Message (Optional) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-ink-muted mb-2">
              <FileText className="w-4 h-4" />
              {t('franchiseApplication.message', 'Mensaje (Opcional)')}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={t('franchiseApplication.messagePlaceholder', 'Cuéntanos sobre ti y por qué te interesa abrir una franquicia Monkey Barber...')}
              rows={4}
              className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition resize-none"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('franchiseApplication.sending', 'Enviando...')}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {t('franchiseApplication.submit', 'Enviar Solicitud')}
              </>
            )}
          </button>

          <p className="text-center text-xs text-ink-subtle">
            {t('franchiseApplication.privacy', 'Al enviar este formulario, aceptas que procesemos tus datos para evaluar tu solicitud de franquicia.')}
          </p>
        </form>
      </div>
    </div>
  );
};

export default FranchiseApplicationModal;
